import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  ArrowRight, Lightbulb, Users, Target, Shield, Rocket, Eye, Heart,
  Building2, Globe, Award, TrendingUp, Calendar
} from 'lucide-react';
import {
  aboutHeroData, aboutStatsData, storyData, nameOriginData, timelineData,
  missionVisionData, valuesData, teamData, partnersLogos, closingData
} from '../../data/aboutData';

const iconMap = {
  Lightbulb, Users, Target, Shield
};

// Hero Section
const AboutHero = () => (
  <section className="relative pt-32 pb-20 overflow-hidden bg-dark-ellipse">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />
    </div>
    <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8bcaf9]/30 rounded-full blur-3xl animate-pulse" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-6 border border-white/20">
          {aboutHeroData.tagline}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
          {aboutHeroData.title}
        </h1>
        <p className="text-lg sm:text-xl text-white/80 leading-relaxed">
          {aboutHeroData.subtitle}
        </p>
      </div>

      {/* Stats */}
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {aboutStatsData.map((stat, idx) => (
          <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105">
            <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</p>
            <p className="text-white/70 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
      </svg>
    </div>
  </section>
);

// Story Section
const StorySection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            De Leire a Asisant
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            {storyData.title}
          </h2>
          {storyData.paragraphs.map((paragraph, idx) => (
            <p key={idx} className="text-[#c3c3c5] leading-relaxed mb-4">
              {paragraph}
            </p>
          ))}
        </div>
        <div className="relative">
          <div className="bg-gradient-to-br from-[#1297fd] to-[#40aaf8] rounded-3xl p-8 text-white">
            <div className="text-center mb-6">
              <p className="text-white/70 text-sm mb-2">Evolución</p>
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">Leire</p>
                  <p className="text-white/60 text-xs">2019</p>
                </div>
                <ArrowRight className="w-6 h-6 text-white/60" />
                <div className="text-center">
                  <p className="text-2xl font-bold">Asisant</p>
                  <p className="text-white/60 text-xs">2026</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-white/10 rounded-xl">
                <Globe className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-2xl font-bold">4</p>
                <p className="text-white/70 text-xs">Mercados</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl">
                <Building2 className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-2xl font-bold">25+</p>
                <p className="text-white/70 text-xs">Empresas</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl">
                <Award className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-2xl font-bold">7</p>
                <p className="text-white/70 text-xs">Años experiencia</p>
              </div>
              <div className="text-center p-4 bg-white/10 rounded-xl">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-80" />
                <p className="text-2xl font-bold">99.9%</p>
                <p className="text-white/70 text-xs">Uptime</p>
              </div>
            </div>
          </div>
          
          {/* Partner logos */}
          <div className="mt-8 flex items-center justify-center gap-8">
            {partnersLogos.map((partner) => (
              <img 
                key={partner.name}
                src={partner.logo} 
                alt={partner.name} 
                className="h-8 opacity-60 hover:opacity-100 transition-opacity"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Timeline Section
const TimelineSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Cronología
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Nuestro{' '}
          <span className="text-[#1297fd]">camino</span>
        </h2>
      </div>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#e3e4e6] hidden md:block" />
        
        <div className="space-y-12">
          {timelineData.map((item, idx) => (
            <div key={item.year} className={`relative flex items-center ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
              {/* Content */}
              <div className={`w-full md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                <div className={`bg-white rounded-2xl p-6 shadow-lg border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl ${item.year === '2026' ? 'ring-2 ring-[#1297fd]' : ''}`}>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold mb-3 ${item.year === '2026' ? 'bg-[#1297fd] text-white' : 'bg-[#8bcaf9]/20 text-[#1297fd]'}`}>
                    {item.year}
                  </span>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-[#c3c3c5] text-sm">{item.description}</p>
                </div>
              </div>
              
              {/* Center dot */}
              <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex">
                <div className={`w-4 h-4 rounded-full border-4 border-white ${item.year === '2026' ? 'bg-[#1297fd]' : 'bg-[#8bcaf9]'}`} />
              </div>
              
              {/* Empty space for alternating layout */}
              <div className="hidden md:block md:w-1/2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Name Origin Section
const NameOriginSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-8" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
        <Rocket className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
        {nameOriginData.title}
      </h2>
      <p className="text-lg text-[#c3c3c5] leading-relaxed max-w-3xl mx-auto">
        {nameOriginData.description}
      </p>
    </div>
  </section>
);

// Mission & Vision Section
const MissionVisionSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Propósito
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
          Misión y visión
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Mission */}
        <div className="bg-gradient-to-br from-[#1297fd] to-[#40aaf8] rounded-3xl p-8 text-white">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
            <Target className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-2xl font-bold mb-4">{missionVisionData.mission.title}</h3>
          <p className="text-white/80 leading-relaxed">{missionVisionData.mission.description}</p>
        </div>

        {/* Vision */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
            <Eye className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-2xl font-bold mb-4">{missionVisionData.vision.title}</h3>
          <p className="text-white/70 leading-relaxed">{missionVisionData.vision.description}</p>
        </div>
      </div>
    </div>
  </section>
);

// Values Section
const ValuesSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Nuestros Valores
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Nuestra filosofía{' '}
          <span className="text-[#1297fd]">guía</span>
        </h2>
        <p className="text-lg text-[#c3c3c5] max-w-2xl mx-auto">
          En Asisant, hemos desarrollado un enfoque basado en la precisión, la integración perfecta y la adaptabilidad para asegurar el éxito a largo plazo de nuestros clientes.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {valuesData.map((value) => {
          const IconComponent = iconMap[value.icon];
          return (
            <div key={value.id} className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#8bcaf9] transition-all hover:shadow-xl hover:-translate-y-2">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
              </div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#1297fd] transition-colors">{value.title}</h3>
              <p className="text-[#c3c3c5] text-sm leading-relaxed">{value.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Team Section
const TeamSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
          Liderazgo
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
          Equipo{' '}
          <span className="text-[#1297fd]">directivo</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {teamData.map((member) => (
          <div key={member.id} className="group text-center">
            <div className="relative mb-6">
              <div className="w-32 h-32 mx-auto rounded-2xl bg-gradient-to-br from-[#1297fd] to-[#40aaf8] flex items-center justify-center group-hover:scale-105 transition-transform shadow-lg">
                <Users className="w-12 h-12 text-white/80" />
              </div>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-1">{member.name}</h3>
            <p className="text-[#1297fd] font-medium text-sm mb-3">{member.role}</p>
            <p className="text-[#c3c3c5] text-sm leading-relaxed">{member.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Closing Section
const ClosingSection = () => (
  <section className="py-24 bg-[#e3e4e6]/30">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-xl text-gray-700 leading-relaxed mb-12">
        {closingData.text}
      </p>
      <img 
        src="https://customer-assets.emergentagent.com/job_cloudsmart/artifacts/pycvljb9_icon_hd_1.png"
        alt="Asisant"
        className="w-20 h-20 mx-auto opacity-80"
      />
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-24 bg-dark-ellipse">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
        ¿Listo para transformar tu negocio?
      </h2>
      <p className="text-lg text-white/80 mb-10">
        Únete a las empresas que ya confían en Asisant para escalar sus operaciones cloud.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/contacto?source=demo">
          <Button
            size="lg"
            className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-10 py-6 rounded-full transition-all hover:scale-105 group"
          >
            Solicitar demo
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
        <Link to="/contacto?source=ventas">
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-full transition-all hover:scale-105 bg-transparent"
          >
            Contactar ventas
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Main Page Component
const AboutPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sobre nosotros · Del laboratorio a plataforma cloud global"
        description="De Leire a asisant: 7+ años construyendo tecnología cloud, 15+ clientes empresariales y más de 45 países cubiertos. Conoce nuestra historia, misión e impacto."
        keywords="asisant, sobre nosotros, historia, Leire, CSP, cloud, equipo"
        canonical="/sobre-nosotros"
      />
      <Navbar />
      <AboutHero />
      <StorySection />
      <TimelineSection />
      <NameOriginSection />
      <MissionVisionSection />
      <ValuesSection />
      <TeamSection />
      <ClosingSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default AboutPage;
